<template>
  <div class="console-page">
    <div class="console-page__header">
      <div>
        <h1 class="console-page__title">Settings</h1>
        <p class="console-page__placeholder">
          Account settings for <strong>{{ username }}</strong>
        </p>
      </div>
    </div>

    <section class="console-panel">
      <h2 class="settings__section-title">Change Password</h2>

      <div v-if="isDemoUser" class="alert alert--info">
        Password changes are not available for the demo account.
      </div>

      <form v-else class="settings__form" @submit.prevent="handleChangePassword">
        <label class="settings__field">
          <span class="console-filter__label">Current Password</span>
          <input
            v-model="currentPassword"
            type="password"
            class="form-input"
            autocomplete="current-password"
            required
            :disabled="submitting"
          />
        </label>

        <label class="settings__field">
          <span class="console-filter__label">New Password</span>
          <input
            v-model="newPassword"
            type="password"
            class="form-input"
            autocomplete="new-password"
            minlength="8"
            required
            :disabled="submitting"
          />
          <span v-if="newPassword && newPassword.length < 8" class="settings__hint settings__hint--warn">
            Must be at least 8 characters ({{ newPassword.length }}/8)
          </span>
        </label>

        <label class="settings__field">
          <span class="console-filter__label">Confirm New Password</span>
          <input
            v-model="confirmPassword"
            type="password"
            class="form-input"
            autocomplete="new-password"
            minlength="8"
            required
            :disabled="submitting"
          />
          <span v-if="confirmPassword && newPassword !== confirmPassword" class="settings__hint settings__hint--warn">
            Passwords do not match
          </span>
        </label>

        <div v-if="error" class="alert alert--error">{{ error }}</div>
        <div v-if="success" class="alert alert--success">{{ success }}</div>

        <div class="settings__actions">
          <button
            type="submit"
            class="button button--primary"
            :disabled="submitting || !isFormValid"
          >
            {{ submitting ? 'Changing...' : 'Change Password' }}
          </button>
        </div>
      </form>
    </section>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useAuth } from '@/composables/useAuth.js'
import { useOrchestratorRequest } from '@/composables/useOrchestratorRequest.js'

const { username } = useAuth()
const { request } = useOrchestratorRequest()

const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const submitting = ref(false)
const error = ref('')
const success = ref('')

const isDemoUser = computed(() => username.value === 'demo')

const isFormValid = computed(() => {
  return (
    currentPassword.value.length > 0 &&
    newPassword.value.length >= 8 &&
    newPassword.value === confirmPassword.value
  )
})

async function handleChangePassword() {
  error.value = ''
  success.value = ''

  if (newPassword.value !== confirmPassword.value) {
    error.value = 'New passwords do not match.'
    return
  }

  if (newPassword.value.length < 8) {
    error.value = 'New password must be at least 8 characters.'
    return
  }

  submitting.value = true

  try {
    const response = await request('/auth/change-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        currentPassword: currentPassword.value,
        newPassword: newPassword.value,
      }),
    })

    if (response.ok) {
      success.value = 'Password changed successfully.'
      currentPassword.value = ''
      newPassword.value = ''
      confirmPassword.value = ''
    } else if (response.status === 403) {
      error.value = 'This account is not allowed to change its password.'
    } else if (response.status === 404) {
      error.value = 'Current password is incorrect.'
    } else {
      error.value = response.data?.message || 'Failed to change password.'
    }
  } catch (fetchError) {
    error.value = 'Network error. Please try again.'
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.settings__section-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 16px;
}

.settings__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 400px;
}

.settings__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.settings__actions {
  margin-top: 8px;
}

.settings__hint {
  font-size: 0.78rem;
  line-height: 1.3;
}

.settings__hint--warn {
  color: var(--status-attention);
}
</style>
